/**
 * apiErrorLog — buffer roulant des erreurs API pour aider au débuggage.
 *
 * Pourquoi : sur le frontend, on ne peut pas écrire dans `history.log` comme
 * côté Node. À la place, on garde les N dernières erreurs API en mémoire +
 * `localStorage`, qu'un développeur peut consulter dans la console ou exporter
 * en JSON pour reproduction.
 *
 * Couverture (deux étages, complémentaires) :
 *   1. Hook beforeError de ky (api-methods.ts) — capte tout ce qui passe par
 *      kyInstance (mutateData, fetchData, encrypted helpers, etc.)
 *   2. Patch global de window.fetch (ce fichier) — capte les hooks qui
 *      appellent `fetch()` directement, sans passer par api-methods.ts
 *      (ex: hooks de lecture directe). Filtré sur
 *      l'URL pour ne capter que les appels métier (BASE_URL ou préfixe proxy).
 *
 * Sécurité : on est en domaine bancaire. Les payloads (request body, response
 * body, headers) sont sanitizés avant stockage :
 *   - clés sensibles (authorization, x-api-key, password, token, pin, cvv,
 *     secret, encrypted, tag) → `[redacted]`
 *   - chaînes de plus de 500 caractères → tronquées
 *   - profondeur d'objet limitée
 *   - les bodies 4xx (400–499, sauf 401) sont toujours capturés (sanitizés) pour
 *     enrichir le rapport support ; 401 exclu (déconnexion automatique)
 *     (flag explicite — désactivé par défaut en prod pour les postes partagés)
 *
 * Utilisation côté dev (console DevTools) :
 *   __apiErrorLog                  // dernières erreurs
 *   __downloadApiErrorLog()        // exporte en JSON
 *   __clearApiErrorLog()           // vide le buffer
 *   __setApiErrorLogVerbose(true)  // capture aussi request/response bodies
 */

import { CONSTANTS } from "./constants";

const STORAGE_KEY = "api-error-log";
const VERBOSE_FLAG_KEY = "api-error-log-verbose";
const MAX_ENTRIES = 50;
const MAX_STRING_LENGTH = 500;
const MAX_DEPTH = 4;

/** Statuts exclus du tampon et des rapports support (faux positifs connus). */
const IGNORED_REPORT_STATUSES = new Set([401]);

const SENSITIVE_KEY_FRAGMENTS = [
  "authorization",
  "x-api-key",
  "xapikey",
  "cookie",
  "password",
  "secret",
  "token",
  "pin",
  "cvv",
  "encrypted",
  "tag",
];

export interface ApiErrorEntry {
  timestamp: string;
  appVersion?: string;
  method?: string;
  url?: string;
  status?: number;
  errorCode?: string | number;
  message?: string;
  axiosCode?: string;
  responseData?: unknown;
  requestData?: unknown;
}

function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase();
  return SENSITIVE_KEY_FRAGMENTS.some((s) => lower.includes(s));
}

function sanitize(value: unknown, depth = 0): unknown {
  if (depth > MAX_DEPTH) return "[depth-limit]";
  if (value === null || value === undefined) return value;
  if (typeof value === "string") {
    return value.length > MAX_STRING_LENGTH
      ? `${value.slice(0, MAX_STRING_LENGTH)}…[truncated]`
      : value;
  }
  if (typeof value !== "object") return value;
  if (value instanceof FormData) return "[FormData]";
  if (value instanceof Blob) return "[Blob]";
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((v) => sanitize(v, depth + 1));
  }
  const obj = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    out[k] = isSensitiveKey(k) ? "[redacted]" : sanitize(v, depth + 1);
  }
  return out;
}

function isVerbose(): boolean {
  try {
    return localStorage.getItem(VERBOSE_FLAG_KEY) === "true";
  } catch {
    return false;
  }
}

/** Vrai si l'erreur ne doit pas figurer dans le tampon ni les rapports support. */
export function isIgnoredApiErrorStatus(status?: number): boolean {
  return status !== undefined && IGNORED_REPORT_STATUSES.has(status);
}

/** Capture les bodies si mode verbeux actif ou erreur client HTTP (4xx, hors 401). */
export function shouldCaptureBodies(
  status?: number,
  verbose?: boolean
): boolean {
  if (isIgnoredApiErrorStatus(status)) return false;
  if (verbose) return true;
  if (status === undefined) return false;
  return status >= 400 && status < 500;
}

/** Parse un body string (JSON) avant log pour que sanitize() rédige les clés sensibles. */
function parseBodyForLog(body: unknown): unknown {
  if (typeof body !== "string") return body;
  if (!body.trim()) return undefined;
  try {
    return JSON.parse(body) as unknown;
  } catch {
    return body;
  }
}

/** Lit le body d'une Request sans consommer l'original (via clone). */
export async function readRequestBody(request: Request): Promise<unknown> {
  try {
    const text = await request.clone().text();
    if (!text.trim()) return undefined;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  } catch {
    return undefined;
  }
}

/** Lit le body d'une Response sans consommer l'original (via clone). */
export async function readResponseBody(response: Response): Promise<unknown> {
  try {
    const text = await response.clone().text();
    if (!text.trim()) return undefined;
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  } catch {
    return undefined;
  }
}

function extractBackendMessage(
  responseData?: { message?: unknown } & Record<string, unknown>
): string | undefined {
  const raw = responseData?.message;
  if (typeof raw === "string" && raw.trim()) {
    return raw.slice(0, MAX_STRING_LENGTH);
  }
  return undefined;
}

function readBuffer(): ApiErrorEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as ApiErrorEntry[]) : [];
  } catch {
    return [];
  }
}

function writeBuffer(entries: ApiErrorEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // localStorage saturé — on retombe sur la moitié la plus récente
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(entries.slice(-Math.floor(MAX_ENTRIES / 2)))
      );
    } catch {
      /* on abandonne silencieusement */
    }
  }
}

interface AxiosLikeError {
  config?: { method?: string; url?: string; data?: unknown };
  response?: {
    status?: number;
    statusCode?: number;
    data?: { code?: string | number; message?: unknown } & Record<
      string,
      unknown
    >;
  };
  code?: string;
  message?: string;
}

interface FetchLikeError {
  url?: string;
  method?: string;
  status?: number;
  message?: string;
  responseData?: unknown;
  requestData?: unknown;
}

/**
 * Enregistre une erreur API dans le buffer. Accepte aussi bien une erreur axios
 * qu'un objet plat construit par l'appelant (utile pour les fonctions à base
 * de `fetch` qui ne lèvent pas la même forme d'erreur qu'axios).
 */
export function logApiError(error: AxiosLikeError | FetchLikeError): void {
  if (!error) return;

  const verbose = isVerbose();
  const axiosErr = error as AxiosLikeError;
  const fetchErr = error as FetchLikeError;

  const status =
    axiosErr.response?.status ??
    axiosErr.response?.statusCode ??
    fetchErr.status;

  // 401 = session expirée / déconnexion auto — faux positif pour le support
  if (isIgnoredApiErrorStatus(status)) return;

  const responseData = axiosErr.response?.data ?? fetchErr.responseData;
  const backendMessage = extractBackendMessage(
    responseData &&
      typeof responseData === "object" &&
      !Array.isArray(responseData)
      ? (responseData as { message?: unknown } & Record<string, unknown>)
      : undefined
  );
  const genericMessage =
    typeof error.message === "string"
      ? error.message.slice(0, MAX_STRING_LENGTH)
      : undefined;

  const entry: ApiErrorEntry = {
    timestamp: new Date().toISOString(),
    appVersion: import.meta.env.VITE_APP_VERSION,
    method:
      (axiosErr.config?.method || fetchErr.method || "").toUpperCase() ||
      undefined,
    url: axiosErr.config?.url || fetchErr.url,
    status,
    errorCode: axiosErr.response?.data?.code,
    message: backendMessage ?? genericMessage,
    axiosCode: axiosErr.code,
  };

  if (shouldCaptureBodies(status, verbose)) {
    entry.responseData = sanitize(responseData);
    entry.requestData = sanitize(axiosErr.config?.data ?? fetchErr.requestData);
  }

  const buffer = readBuffer();
  buffer.push(entry);
  const trimmed = buffer.slice(-MAX_ENTRIES);
  writeBuffer(trimmed);

  // Exposition console en DEV pour inspection rapide
  if (import.meta.env.DEV && typeof window !== "undefined") {
    (window as unknown as Record<string, unknown>).__apiErrorLog = trimmed;
  }
}

export function getApiErrorLog(): ApiErrorEntry[] {
  return readBuffer();
}

/** Entrées exploitables pour le rapport support (401 exclus). */
export function getReportableApiErrorLog(): ApiErrorEntry[] {
  return readBuffer().filter((e) => !isIgnoredApiErrorStatus(e.status));
}

export function clearApiErrorLog(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined") {
    (window as unknown as Record<string, unknown>).__apiErrorLog = [];
  }
}

export function setApiErrorLogVerbose(enabled: boolean): void {
  try {
    if (enabled) localStorage.setItem(VERBOSE_FLAG_KEY, "true");
    else localStorage.removeItem(VERBOSE_FLAG_KEY);
  } catch {
    /* ignore */
  }
}

export function downloadApiErrorLog(): void {
  if (typeof document === "undefined") return;
  const entries = readBuffer();
  const blob = new Blob([JSON.stringify(entries, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `api-errors-${new Date()
    .toISOString()
    .replace(/[:.]/g, "-")}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Patch global de window.fetch
// ---------------------------------------------------------------------------

/**
 * Vrai si l'URL appartient à notre API métier — on filtre pour éviter de logger
 * `api.ipify.org`, `version.json`, blob URL locales, etc.
 * Couvre les appels absolus (BASE_URL) et les appels relatifs via le proxy
 * vite de dev (VITE_PROXY_BASE_URL, `/app` par défaut).
 */
function shouldLogUrl(url: string | undefined): boolean {
  if (!url) return false;
  if (url.startsWith("blob:") || url.startsWith("data:")) return false;
  const base = (CONSTANTS.BASE_URL || "").replace(/\/$/, "");
  if (base && url.startsWith(base)) return true;
  // Proxy vite (dev/preview) : mêmes règles de normalisation que vite.config.ts
  // ("/" initial et final exigés pour ne pas matcher des préfixes plus larges).
  const proxyPath = import.meta.env.VITE_PROXY_BASE_URL || "/app";
  const proxyRaw = proxyPath.startsWith("/") ? proxyPath : `/${proxyPath}`;
  const proxyPrefix = proxyRaw.endsWith("/") ? proxyRaw : `${proxyRaw}/`;
  if (url.startsWith(proxyPrefix)) return true;
  return false;
}

function extractUrl(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function extractMethod(
  input: RequestInfo | URL,
  init?: RequestInit
): string | undefined {
  if (init?.method) return init.method.toUpperCase();
  if (typeof input !== "string" && !(input instanceof URL) && input.method) {
    return input.method.toUpperCase();
  }
  return "GET";
}

/**
 * Pose un wrapper sur window.fetch qui logue les erreurs API.
 *
 * Mitigations :
 *   - HMR : guard via window.__apiErrorLogFetchPatched pour éviter le double wrap
 *   - AbortError : on ignore les annulations (TanStack Query, démontages)
 *   - URL filter : on ne logue que les appels qui matchent BASE_URL ou /app
 *   - Body : la réponse originale n'est pas consommée (lecture via clone)
 */
function patchGlobalFetch(): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as Record<string, unknown>;
  if (w.__apiErrorLogFetchPatched) return;
  if (typeof window.fetch !== "function") return;

  const originalFetch = window.fetch.bind(window);

  const patched: typeof window.fetch = async (input, init) => {
    const url = extractUrl(input);
    const method = extractMethod(input, init);
    const eligible = shouldLogUrl(url);

    let response: Response;
    try {
      response = await originalFetch(input as RequestInfo, init);
    } catch (err: unknown) {
      const e = err as { name?: string; message?: string };
      // Annulations TanStack Query / unmount → pas une vraie erreur métier
      if (eligible && e?.name !== "AbortError") {
        logApiError({
          url,
          method,
          message: e?.message,
          requestData: parseBodyForLog(init?.body),
        });
      }
      throw err;
    }

    if (eligible && !response.ok && !isIgnoredApiErrorStatus(response.status)) {
      const verbose = isVerbose();
      const captureBodies = shouldCaptureBodies(response.status, verbose);
      let responseData: unknown;
      if (captureBodies) {
        responseData = await readResponseBody(response);
      }
      logApiError({
        url,
        method,
        status: response.status,
        message: response.statusText,
        requestData: parseBodyForLog(init?.body),
        responseData,
      });
    }

    return response;
  };

  window.fetch = patched;
  w.__apiErrorLogFetchPatched = true;
}

// Expose les helpers en console pour faciliter le débuggage
// (toujours, pas seulement en DEV — utile en UAT pour faire remonter un cas)
if (typeof window !== "undefined") {
  const w = window as unknown as Record<string, unknown>;
  w.__apiErrorLog = readBuffer();
  w.__downloadApiErrorLog = downloadApiErrorLog;
  w.__clearApiErrorLog = clearApiErrorLog;
  w.__setApiErrorLogVerbose = setApiErrorLogVerbose;
  patchGlobalFetch();
}
